import { TCreativeSummary } from "domain/entities/creative-summary-response";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createBlobDownloader } from "utils/files/downloadBlob";
import { IReportsRepository } from "../../../domain/repositories/reports-repository";
import { ReportsFilterContext } from "./ReportsFilterProvider";
import { checkParamsCampaignOnly } from "helper/filtersHelper";

type TCreativeSummaryProviderProps = {
  requestParamsIndex: number;
  children: React.ReactNode;
  reportsDatasource: IReportsRepository;
};

export type TCreativeSummaryState = {
  requestParamsIndex: number;
  creativeSummaryData?: TCreativeSummary;
  downloadingReports?: boolean;
  downloadCreativeSummaryReport: () => void;
  isLoading: boolean;
};

const initContext: TCreativeSummaryState = {
  requestParamsIndex: 0,
  downloadCreativeSummaryReport: () => null,
  isLoading: true,
};
export const CreativeSummaryContext = createContext(initContext);

export const CreativeSummaryProvider = ({
  requestParamsIndex,
  children,
  reportsDatasource,
}: TCreativeSummaryProviderProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [creativeSummaryData, setCreativeSummaryData] = useState<any>();
  const [downloadingReports, setDownloadingReports] = useState(false);
  const { requestParams, comparisonEnabled } = useContext(ReportsFilterContext);
  const currentRequestParams = requestParams[requestParamsIndex];

  const downloadCreativeSummaryReport = useCallback(() => {
    setDownloadingReports(true);
    return reportsDatasource
      .exportCreativeSummaryCSV(currentRequestParams)
      .then(createBlobDownloader("creative-summary.zip"))
      .finally(() => setDownloadingReports(false));
  }, [currentRequestParams]);

  const canShowPanel = useMemo(() => {
    if (comparisonEnabled) {
      return requestParams.every(checkParamsCampaignOnly);
    }

    return checkParamsCampaignOnly(currentRequestParams);
  }, [requestParams]);

  useEffect(() => {
    if (canShowPanel) {
      setIsLoading(true);
      reportsDatasource
        .getCreativeSummary(currentRequestParams)
        .then(setCreativeSummaryData)
        .then(() => setIsLoading(false));
    }
  }, [currentRequestParams]);

  return (
    <CreativeSummaryContext.Provider
      value={{
        requestParamsIndex,
        downloadingReports,
        creativeSummaryData,
        downloadCreativeSummaryReport,
        isLoading,
      }}
    >
      {canShowPanel ? children : <></>}
    </CreativeSummaryContext.Provider>
  );
};
